/**
 * Observability bootstrap — PostHog + Sentry init in one place.
 * Called once from main.tsx before React renders.
 *
 * Env-value contract: if a value is empty OR still contains a `REPLACE_ME`
 * placeholder from `.env.example`, the corresponding SDK is skipped (no init,
 * no requests, no console noise). Swap in a real value + restart Vite to
 * activate the SDK.
 */
import posthog from 'posthog-js';
import * as Sentry from '@sentry/react';

const POSTHOG_KEY = import.meta.env.VITE_POSTHOG_KEY;
const POSTHOG_HOST = import.meta.env.VITE_POSTHOG_HOST;
const SENTRY_DSN = import.meta.env.VITE_SENTRY_DSN;

/**
 * Returns true if the env value is present AND not a `.env.example` placeholder.
 * Keeps dev consoles clean while the project is being scaffolded without real
 * analytics/crash keys.
 */
function isUsableEnvValue(v: string | undefined): v is string {
  return Boolean(v && !v.includes('REPLACE_ME'));
}

/**
 * Generate or load a UUID-only client identifier.
 * Per Q9 of the architecture (minimum-sharing posture): no email, no name,
 * just an opaque identifier so analytics/crash events aren't linkable to a person.
 */
function getOrCreateClientId(): string {
  const KEY = 'pokersharp_client_uuid';
  const existing = localStorage.getItem(KEY);
  if (existing) return existing;
  const uuid = crypto.randomUUID();
  localStorage.setItem(KEY, uuid);
  return uuid;
}

export function initObservability(): void {
  const clientId = getOrCreateClientId();

  if (isUsableEnvValue(POSTHOG_KEY)) {
    posthog.init(POSTHOG_KEY, {
      api_host: POSTHOG_HOST,
      defaults: '2026-01-30',
      // Privacy: no autocapture, no session recording — V1 PRD §10 + Q9 posture.
      autocapture: false,
      disable_session_recording: true,
      // Identify with UUID only.
      loaded: (ph) => {
        ph.identify(clientId);
      },
    });
  } else if (import.meta.env.DEV) {
    console.info(
      '[observability] PostHog SDK skipped — VITE_POSTHOG_KEY is empty or still a placeholder. Set a real key in .env.local to enable.',
    );
  }

  if (isUsableEnvValue(SENTRY_DSN)) {
    Sentry.init({
      dsn: SENTRY_DSN,
      tracesSampleRate: 0.1,
      // PII scrubbing per the architecture's Q9 posture.
      sendDefaultPii: false,
      initialScope: {
        user: { id: clientId },
      },
    });
  } else if (import.meta.env.DEV) {
    console.info(
      '[observability] Sentry SDK skipped — VITE_SENTRY_DSN is empty or still a placeholder. Set a real DSN in .env.local to enable.',
    );
  }
}
